import {
    PortalContainer,
    PortalUrls,
    portalStoreToken,
    usePortalModeOptions,
} from '@servicetitan/cp-ui';
import {
    ActionMenu,
    Avatar,
    BodyText,
    Button,
    Headline,
    Keys,
    Spinner,
    Stack,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { tokens } from '@servicetitan/tokens/core';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { LogoStore } from '../../../auth/stores/logo-store';
import { PortalAuthStore, SignerRole } from '../../../auth/stores/portal-auth.store';
import { validateContactInfo } from '../../../auth/utils/contact-info.validator';
import { useAuthHistory } from '../../../auth/utils/use-auth-history';
import { ShowCompanyNameSettingsStore } from '../../../dashboard/api';
import { TermsOfServiceLink } from '../../../legal-agreement/components/terms-of-service-link';
import { useScrollDirection } from '../../hooks/use-scroll-direction';
import { AppSettingsStore } from '../../stores/app-settings.store';
import { NavigationStore } from '../../stores/navigation.store';
import { WebPortalStore } from '../../stores/portal.store';
import { BannerStyleStatus } from '../../utils/banner-settings';
import { useEntityParams } from '../../utils/direct-link-query-param';
import { SchedulingWrapper } from '../scheduler/scheduling-wrapper';
import { PortalBanner } from './portal-banner';
import { PortalLayoutGrid } from './portal-layout-grid';
import { PortalLayoutMenu } from './portal-layout-menu';
import * as Styles from './portal-layout.module.less';
import { PortalLogo } from './portal-logo';

/*
 * Company name drops to its own row only at this width and below (CPO-4599).
 * Inclusive of 576 so it matches the mobile portal breakpoint's upper edge:
 * `useClientWidthOptions` treats width < 576 as mobile, and 576 itself must
 * still stack.
 */
const COMPANY_NAME_STACK_MAX_WIDTH = 576;
/** EST-13204 path form; legacy `/proposals/presentation` kept for QA bookmarks. */
const AI_PRESENTATION_ROUTE = `${PortalUrls.proposals.root}/:proposalId/presentation`;
const AI_PRESENTATION_LEGACY_PATH = `${PortalUrls.proposals.root}/presentation`;

/*
 * Grid row heights for the non-sidebar header. `auto` lets the row track the
 * header's actual rendered height (padding + content + any wrapped title) so
 * the sticky header never overlaps the first row of page content. `condensed`
 * uses a fixed pixel value because the header animates its height to match
 * via CSS transition.
 */
const HEADER_ROW_HEIGHT = {
    condensed: '45px',
    expanded: 'auto',
} as const;

const getHeaderRowHeight = (condensed: boolean): string =>
    condensed ? HEADER_ROW_HEIGHT.condensed : HEADER_ROW_HEIGHT.expanded;

interface PortalLayoutProps {
    children?: ReactNode;
}

export const PortalLayoutProvider = provide({ singletons: [NavigationStore] })(
    ({ children }) => children
);

export const PortalLayoutComponent: FC<PortalLayoutProps> = observer(({ children }) => {
    const [
        {
            authorized,
            authorizedLoginless,
            authorizing,
            brand,
            brandLoading,
            getBrand,
            logout,
            signerRole,
        },
        { directLinksSignUpSignInEnabled },
        { enabled: showCompanyNameSettings, loaded: showCompanyNameSettingsLoaded },
    ] = useDependencies(PortalAuthStore, AppSettingsStore, ShowCompanyNameSettingsStore);
    const isCountersigner = signerRole === SignerRole.Countersigner;
    const [{ setPortalMode, settings, user }] = useDependencies<[WebPortalStore]>(
        portalStoreToken as any
    );
    const [{ displayImageInHeader, isLoading: logoLoading }] =
        useDependencies<[LogoStore]>(LogoStore);
    const authHistory = useAuthHistory();
    const location = useLocation();
    const { entityId } = useEntityParams();
    const isAiPresentation =
        Boolean(matchPath({ path: AI_PRESENTATION_ROUTE, end: true }, location.pathname)) ||
        location.pathname === AI_PRESENTATION_LEGACY_PATH ||
        location.pathname === `${AI_PRESENTATION_LEGACY_PATH}/`;

    const [userContextOpen, setUserContextOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const mode = usePortalModeOptions();
    const [mainScrollRoot, setMainScrollRoot] = useState<HTMLDivElement | null>(null);
    const bindMainScrollRef = useCallback((node: HTMLDivElement | null) => {
        setMainScrollRoot(node);
    }, []);
    const { condensed: scrollCondensed } = useScrollDirection(mainScrollRoot);
    /*
     * Every viewport without the sidebar (mobile, tablet, desktop-narrow) uses
     * the compact header: x-small controls, small avatar, condense-on-scroll.
     */
    const compactHeader = !mode.sidebarMenu;
    const condensed = compactHeader && scrollCondensed;
    /*
     * Logged-in and logged-out share one layout (CPO-4599): the company name
     * stays on the controls row above 576px, and both drop it to its own row
     * at 576px and below. Scroll still hides it on every non-sidebar width.
     * Measured the same way as portal mode (document.body), not window.innerWidth,
     * so a scrollbar doesn't flip the breakpoint early.
     */
    const [viewportWidth, setViewportWidth] = useState(
        () => document.body.getBoundingClientRect().width
    );
    useEffect(() => {
        const update = () => setViewportWidth(document.body.getBoundingClientRect().width);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);
    const stacked = compactHeader && viewportWidth <= COMPANY_NAME_STACK_MAX_WIDTH;

    useEffect(() => {
        setPortalMode(mode);
    }, [mode, setPortalMode]);

    useEffect(() => {
        if (authorizedLoginless && entityId) {
            getBrand(entityId);
        }
    }, [authorizedLoginless, entityId, getBrand]);

    const onSignOut = () => {
        setUserContextOpen(false);
        setMenuOpen(false);
        logout();
    };

    const closeMenu = () => {
        setMenuOpen(false);
    };

    const toggleUserMenu = () => {
        setUserContextOpen(!userContextOpen);
    };

    const handleAvatarKeyPress = (e: KeyboardEvent) => {
        if ([Keys.Enter, Keys.Space].includes(e.code as Keys)) {
            toggleUserMenu();
        }
    };

    const getLoginPath = () => {
        const path = location.pathname + location.search;
        const loginPath = authHistory.path(
            'login',
            undefined,
            path && path !== '/' ? { redirect: encodeURI(path) } : undefined
        );

        if (!settings?.host) {
            return loginPath;
        }
        return `${window.location.protocol}//${settings.host}${loginPath}`;
    };

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty brand.officialName must fall back to the portal setting, which ?? would not do
    const title = brand?.officialName ? brand.officialName : settings?.title;
    const isLoading = authorizing || (authorizedLoginless && entityId && brandLoading);
    /*
     * Accounts created with a phone number as their primary key have no real
     * display name — `user.name` is the phone number itself. Showing that in
     * the header avatar (as initials derived from digits) is meaningless, so
     * fall back to a generic person icon instead.
     */
    const isPhoneShapedName = validateContactInfo(user?.name ?? '', 'either').kind === 'phone';

    if (isLoading) {
        return <Spinner size="tiny" />;
    }

    const mainContent = (
        <div className={Styles.contentWrapper}>
            <div
                className={Styles.contentWrapperInner}
                data-tracking-section="portal-layout-content"
                data-cy="portal-layout-content"
            >
                {children}
            </div>
        </div>
    );

    /*
     * AI presentations deliberately use the Customer Portal only as their
     * delivery shell. Keep its auth, settings, and MFE context, but remove all
     * portal chrome so the experience reads as a standalone proposal.
     */
    if (isAiPresentation) {
        return (
            <PortalContainer mode={mode} className={classNames('min-h-100vh')}>
                <div className={Styles.presentationLayout} data-cy="ai-presentation-layout">
                    {mainContent}
                </div>
            </PortalContainer>
        );
    }

    const sideNav = (
        <div className={Styles.contentWrapper}>
            <PortalLayoutMenu
                authorized={authorized}
                menuOpen={menuOpen}
                mode={mode}
                onClose={closeMenu}
                onSignOut={onSignOut}
                brand={brand}
            />
        </div>
    );

    const header = (
        <div
            className={Styles.header}
            data-tracking-section="portal-layout-header"
            data-cy="portal-layout-header"
            data-condensed={condensed ? 'true' : undefined}
            data-stacked={stacked ? 'true' : undefined}
        >
            {!authorizedLoginless && compactHeader && (
                <Button
                    className={Styles.hamburger}
                    iconName="menu"
                    fill="subtle"
                    size="xsmall"
                    onClick={() => setMenuOpen(true)}
                    aria-label="Menu"
                />
            )}

            {!logoLoading &&
                (compactHeader
                    ? showCompanyNameSettingsLoaded && showCompanyNameSettings !== false
                    : displayImageInHeader) && <PortalLogo className={Styles.logoHeader} />}

            {/* Fills the first row in stacked mode so the trailing controls stay right-aligned even without a logo. */}
            <div className={Styles.headerSpacer} aria-hidden="true" />

            <Stack className={Styles.headerContent}>
                <Headline
                    size={compactHeader ? 'small' : 'medium'}
                    className={classNames('m-b-0', Styles.headerTitle)}
                    data-cy="portal-layout-header-title"
                >
                    {showCompanyNameSettingsLoaded && showCompanyNameSettings !== false && title}
                </Headline>
            </Stack>

            {!authorized &&
                !isCountersigner &&
                directLinksSignUpSignInEnabled &&
                settings?.host && (
                    <Button
                        fill="outline"
                        size={compactHeader ? 'xsmall' : 'small'}
                        href={getLoginPath()}
                    >
                        Access customer portal
                    </Button>
                )}

            {authorized && settings?.bookingEnabled && (
                <SchedulingWrapper compact={compactHeader} />
            )}

            {authorized && (
                <ActionMenu
                    open={userContextOpen}
                    onClickOutside={userContextOpen ? () => setUserContextOpen(false) : undefined}
                    trigger={
                        <Avatar
                            name={isPhoneShapedName ? undefined : user?.name}
                            icon={isPhoneShapedName ? 'person' : undefined}
                            color={tokens.colorBlueGrey600}
                            size={compactHeader ? 's' : 'm'}
                            className={classNames('cursor-pointer', Styles.avatar)}
                            onClick={toggleUserMenu}
                            onKeyPress={handleAvatarKeyPress}
                            aria-label="User"
                            tabIndex="0"
                            data-testid="user-button"
                        />
                    }
                >
                    <BodyText className={classNames('p-l-3 p-x-1', Styles.userName)}>
                        {user?.name}
                    </BodyText>
                    <ActionMenu.Group>
                        <ActionMenu.Item
                            onClick={onSignOut}
                            data-tracking-id="signout-button"
                            className="m-b-1"
                        >
                            <BodyText bold className="c-neutral-200">
                                Sign out
                            </BodyText>
                        </ActionMenu.Item>
                    </ActionMenu.Group>
                    <Stack
                        className={Styles.termsContainer}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <TermsOfServiceLink />
                    </Stack>
                </ActionMenu>
            )}
        </div>
    );

    return (
        <PortalContainer mode={mode} className={classNames('min-h-100vh')}>
            <PortalLayoutGrid
                alertBanner={
                    authorized ? <PortalBanner style={BannerStyleStatus.Alert} /> : undefined
                }
                announcementBanner={
                    authorized ? <PortalBanner style={BannerStyleStatus.Announcement} /> : undefined
                }
                header={header}
                sideNav={sideNav}
                mainContent={mainContent}
                logoInHeader={logoLoading || displayImageInHeader === true}
                showSidebar={mode.sidebarMenu}
                mainContentRef={bindMainScrollRef}
                headerRowHeight={getHeaderRowHeight(condensed)}
            />

            {/* Render logo offscreen while loading to determine dimensions */}
            {logoLoading && (
                <div style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}>
                    <PortalLogo />
                </div>
            )}
        </PortalContainer>
    );
});

export const PortalLayout: FC<PortalLayoutProps> = props => (
    <PortalLayoutProvider>
        <PortalLayoutComponent {...props} />
    </PortalLayoutProvider>
);
